import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Bind } from "../../../decorators/bind.decorator";
import { s25Resource } from "../../../util/s25.resource";
import { SeSecurityStore } from "../se.security.store";
import { Connect25EmailTemplateApi, type SeEmailTemplateDto } from "./connect25.email.template.api";
import {
    buildEmailTemplatePayload,
    refreshEmailTemplateTable,
    sortEmailTemplatesByName,
} from "./connect25.email.template.form.util";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { dropdownItemsFromIds, idsFromDropdownItems } from "../trails/trail.dropdown.util";
import { TelemetryService } from "../../../services/telemetry.service";
import { StateService } from "../../../services/state.service";
import { S25States } from "../../../services/state/state.map";
import { StateStore } from "../../../services/state/state.store";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";

const NEW_TEMPLATE_ROUTE = "new";

@TypeManagerDecorator("s25-ng-connect25-email-template-admin")
@Component({
    selector: "s25-ng-connect25-email-template-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25RichTextEditorModule,
        S25DropdownPaginatedModule,
        S25TableModule,
        S25FadingSuccessMsgComponent,
    ],
    template: `
        @if (securityStore.canConfigureEmailTemplates()) {
            <div class="connect25-email-template-admin">
                @if (!formVisible()) {
                    <h2>Email Template Settings</h2>
                    <p class="connect25-email-template-admin__intro c-margin-bottom--double">
                        Institution templates are hidden from organizations until you grant access below. An empty
                        organization list means no org can use the template.
                    </p>

                    @if (templatesResource.error()) {
                        <p class="c-text-danger" role="alert">Could not load templates.</p>
                    } @else if (templatesResource.isLoading() && !templatesResource.value()) {
                        <p>Loading…</p>
                    } @else {
                        <div class="connect25-email-template-admin__toolbar c-margin-bottom--single">
                            <button type="button" class="aw-button aw-button--outline" (click)="startCreate()">
                                Create template
                            </button>
                        </div>
                        <div class="connect25-email-template-admin__table-wrap">
                            <s25-ng-table
                                #templatesTable
                                [caption]="'Email templates'"
                                [dataSource]="templatesTableSource"
                                [hasRefresh]="true"
                                [hasFilter]="true"
                                [columnSortable]="true"
                                [showHeaderWhenNoData]="true"
                                [noResultsMessage]="'No institution email templates yet.'"
                                [unlimitedWidth]="true"
                            />
                        </div>
                    }
                } @else {
                    <div class="c-margin-bottom--single">
                        <button type="button" class="aw-button aw-button--outline" (click)="backToList()">
                            ← Back to template list
                        </button>
                    </div>
                    <h2>{{ editingId() ? "Edit template" : "New template" }}</h2>
                    @if (formLoadError()) {
                        <p class="c-text-danger" role="alert">{{ formLoadError() }}</p>
                    }
                    @if (formLoading()) {
                        <p>Loading template…</p>
                    } @else {
                        <div class="connect25-email-template-admin__grid">
                            <label class="connect25-email-template-admin__field">
                                <span class="connect25-email-template-admin__label"
                                    >Template name <span class="c-text-danger">*</span></span
                                >
                                <input
                                    type="text"
                                    class="c-input"
                                    [ngModel]="formTemplateName()"
                                    (ngModelChange)="formTemplateName.set($event)"
                                    required
                                />
                            </label>
                            <label class="connect25-email-template-admin__field">
                                <span class="connect25-email-template-admin__label">Subject</span>
                                <input
                                    type="text"
                                    class="c-input"
                                    [ngModel]="formSubject()"
                                    (ngModelChange)="formSubject.set($event)"
                                />
                            </label>
                            <label
                                class="connect25-email-template-admin__field connect25-email-template-admin__field--checkbox"
                            >
                                <input
                                    type="checkbox"
                                    [ngModel]="formEnabled()"
                                    (ngModelChange)="formEnabled.set($event)"
                                />
                                <span>Enabled</span>
                            </label>
                            <div
                                class="connect25-email-template-admin__field connect25-email-template-admin__field--body"
                            >
                                <span class="connect25-email-template-admin__label">Body</span>
                                <s25-ng-rich-text-editor
                                    class="connect25-email-template-admin__rte"
                                    [modelValue]="formBodyHtml()"
                                    (modelValueChange)="formBodyHtml.set($event)"
                                    [defaultHeight]="220"
                                />
                            </div>
                            @if (editingId()) {
                                <div class="connect25-email-template-admin__field">
                                    <span class="connect25-email-template-admin__label">Organizations with access</span>
                                    <s25-ng-dropdown-multi-search-criteria
                                        class="connect25-email-template-admin__orgs"
                                        [type]="'studentEngagementOrganizations'"
                                        [value]="formOrgAllowlist()"
                                        (valueChange)="onOrgAllowlistChange($event)"
                                        [placeholder]="'Select organizations'"
                                        [hasDone]="false"
                                        [ariaLabel]="'Organizations that may use this template'"
                                    />
                                </div>
                            } @else {
                                <p class="connect25-email-template-admin__hint c-text--secondary">
                                    Save the template first, then edit it to set organization access.
                                </p>
                            }
                        </div>
                        <div class="c-margin-top--double connect25-email-template-admin__actions">
                            <div class="connect25-email-template-admin__action-buttons">
                                <button
                                    type="button"
                                    class="aw-button aw-button--primary"
                                    (click)="save()"
                                    [disabled]="saving()"
                                >
                                    {{ editingId() ? "Save changes" : "Create template" }}
                                </button>
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline c-margin-left--single"
                                    (click)="backToList()"
                                >
                                    Cancel
                                </button>
                            </div>
                            <s25-ng-fading-success-msg
                                class="connect25-email-template-admin__save-success"
                                [(message)]="saveSuccessMessage"
                            />
                        </div>
                    }
                }
            </div>
        }
    `,
    styles: `
        .connect25-email-template-admin h2 {
            margin-bottom: 0.75rem;
            font-size: 1.25rem;
            font-weight: 600;
        }
        .connect25-email-template-admin__intro {
            max-width: 52rem;
        }
        .connect25-email-template-admin__toolbar {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            align-items: center;
        }
        .connect25-email-template-admin__grid {
            display: grid;
            gap: 1rem;
            max-width: 52rem;
        }
        .connect25-email-template-admin__field {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            align-items: flex-start;
        }
        .connect25-email-template-admin__field--body {
            align-self: stretch;
            width: 100%;
        }
        .connect25-email-template-admin__field--checkbox {
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
        }
        .connect25-email-template-admin__label {
            font-weight: 600;
        }
        .connect25-email-template-admin__rte {
            display: block;
            width: 100%;
        }
        :host ::ng-deep .connect25-email-template-admin__rte .tox-tinymce {
            width: 100%;
        }
        .connect25-email-template-admin__orgs {
            width: 100%;
            max-width: 36rem;
        }
        .connect25-email-template-admin__actions {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }
        .connect25-email-template-admin__action-buttons {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }
        .connect25-email-template-admin__save-success {
            min-height: 1.5rem;
        }
    `,
})
export class Connect25EmailTemplateAdminComponent {
    @ViewChild("templatesTable") private templatesTable?: S25TableComponent;

    protected readonly securityStore = inject(SeSecurityStore);
    private readonly stateStore = inject(StateStore);

    private readonly telemetrySent = signal(false);
    private lastSyncedRouteKey: string | undefined;

    readonly templatesResource = s25Resource({
        if: () => this.securityStore.canConfigureEmailTemplates(),
        loader: () => Connect25EmailTemplateApi.listInstitutionTemplates(),
    });

    readonly sortedTemplates = computed(() => sortEmailTemplatesByName(this.templatesResource.value() ?? []));

    readonly templatesTableSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getTemplateTableRows,
        columns: [
            { id: "templateName", header: "Name", minWidth: 160 },
            { id: "subject", header: "Subject", minWidth: 160 },
            { id: "enabled", header: "Enabled", minWidth: 80 },
            GenericTableButtonComponent.Column("Edit", this.onTableEdit, "outline"),
            GenericTableButtonComponent.Column("Delete", this.onTableDelete, "danger--outline"),
        ],
    };

    readonly formVisible = signal(false);
    readonly formLoading = signal(false);
    readonly formLoadError = signal<string | undefined>(undefined);
    readonly saving = signal(false);
    readonly editingId = signal<number | undefined>(undefined);

    readonly formTemplateName = signal("");
    readonly formSubject = signal("");
    readonly formBodyHtml = signal("");
    readonly formEnabled = signal(true);
    readonly formOrgAllowlist = signal<DropDownItem[]>([]);
    readonly saveSuccessMessage = signal("");

    constructor() {
        effect(() => {
            if (!this.securityStore.canConfigureEmailTemplates() || this.telemetrySent()) {
                return;
            }
            this.telemetrySent.set(true);
            TelemetryService.sendWithSub("EmailTemplateSettings", "Admin", "Open");
        });

        effect(() => {
            if (this.stateStore.currentStateName() !== S25States.names.seEmailTemplateSettings) {
                return;
            }
            const raw = this.stateStore.seEmailTemplateId();
            const routeKey = raw == null ? "" : String(raw).trim();
            void this.syncEditorFromRoute(routeKey);
        });
    }

    onOrgAllowlistChange(items: DropDownItem[]): void {
        this.formOrgAllowlist.set(items ?? []);
    }

    @Bind
    async getTemplateTableRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) {
            await this.templatesResource.reloadAndAwait();
        }
        const templates = this.sortedTemplates();
        return {
            rows: templates.map((template) => ({
                id: template.emailTemplateId!,
                name: template.templateName,
                data: { template },
                cells: {
                    templateName: { text: template.templateName ?? "" },
                    subject: { text: template.subject ?? "" },
                    enabled: { text: template.enabled !== false ? "Yes" : "No" },
                },
            })),
        };
    }

    @Bind
    async onTableEdit(row: Table.Row): Promise<void> {
        const template = row.data.template as SeEmailTemplateDto;
        const templateId = template.emailTemplateId;
        if (!templateId) {
            return;
        }
        void StateService.go(S25States.names.seEmailTemplateSettings, { emailTemplateId: String(templateId) }, {});
    }

    @Bind
    async onTableDelete(row: Table.Row): Promise<void> {
        await this.confirmDelete(row.data.template as SeEmailTemplateDto);
    }

    startCreate(): void {
        void StateService.go(S25States.names.seEmailTemplateSettings, { emailTemplateId: NEW_TEMPLATE_ROUTE }, {});
    }

    backToList(): void {
        this.lastSyncedRouteKey = undefined;
        this.clearFormFields();
        this.formVisible.set(false);
        void StateService.go(S25States.names.seEmailTemplateSettings, { emailTemplateId: "" }, { location: "replace" });
    }

    /** Used by route sync and unit tests. */
    enterCreateMode(): void {
        this.clearFormFields();
        this.formVisible.set(true);
    }

    private clearFormFields(): void {
        this.editingId.set(undefined);
        this.formTemplateName.set("");
        this.formSubject.set("");
        this.formBodyHtml.set("");
        this.formEnabled.set(true);
        this.formOrgAllowlist.set([]);
        this.formLoadError.set(undefined);
        this.formLoading.set(false);
        this.saveSuccessMessage.set("");
    }

    private routeTemplateKeyFromStore(): string {
        const raw = this.stateStore.seEmailTemplateId();
        return raw == null ? "" : String(raw).trim();
    }

    private async syncEditorFromRoute(routeKey: string): Promise<void> {
        if (routeKey === this.lastSyncedRouteKey) {
            return;
        }

        if (!routeKey) {
            this.lastSyncedRouteKey = routeKey;
            this.clearFormFields();
            this.formVisible.set(false);
            return;
        }

        if (routeKey === NEW_TEMPLATE_ROUTE) {
            this.lastSyncedRouteKey = routeKey;
            this.enterCreateMode();
            return;
        }

        const templateId = S25Util.parseInt(routeKey);
        if (templateId == null || !Number.isFinite(templateId)) {
            this.lastSyncedRouteKey = routeKey;
            this.clearFormFields();
            this.formLoadError.set("Invalid template id in URL.");
            this.formVisible.set(true);
            return;
        }

        this.lastSyncedRouteKey = routeKey;
        await this.loadEditorForTemplateId(templateId);
    }

    private async loadEditorForTemplateId(templateId: number): Promise<void> {
        this.formVisible.set(true);
        this.formLoading.set(true);
        this.formLoadError.set(undefined);
        this.editingId.set(templateId);
        try {
            const [fullTemplate, allowlistRows] = await Promise.all([
                Connect25EmailTemplateApi.getInstitutionTemplate(templateId),
                Connect25EmailTemplateApi.getOrgAllowlists([templateId]),
            ]);
            this.formTemplateName.set(fullTemplate.templateName ?? "");
            this.formSubject.set(fullTemplate.subject ?? "");
            this.formBodyHtml.set(fullTemplate.bodyHtml ?? "");
            this.formEnabled.set(fullTemplate.enabled !== false);
            const accountIds = allowlistRows.find((entry) => entry.emailTemplateId === templateId)?.accountIds ?? [];
            this.formOrgAllowlist.set(dropdownItemsFromIds(accountIds));
        } catch (caughtError: unknown) {
            const message =
                caughtError instanceof Error && caughtError.message ? caughtError.message : "Could not load template.";
            this.formLoadError.set(message);
        } finally {
            this.formLoading.set(false);
        }
    }

    private applySavedTemplateToForm(saved: SeEmailTemplateDto): void {
        const savedId = saved.emailTemplateId;
        if (savedId != null) {
            this.editingId.set(savedId);
        }
        this.formTemplateName.set(saved.templateName ?? "");
        this.formSubject.set(saved.subject ?? "");
        this.formBodyHtml.set(saved.bodyHtml ?? "");
        this.formEnabled.set(saved.enabled !== false);
        this.formLoadError.set(undefined);
        this.formLoading.set(false);
        this.formVisible.set(true);
    }

    private buildPayload(): SeEmailTemplateDto {
        return buildEmailTemplatePayload({
            templateName: this.formTemplateName(),
            subject: this.formSubject(),
            bodyHtml: this.formBodyHtml(),
            enabled: this.formEnabled(),
            editingId: this.editingId(),
        });
    }

    private async refreshTemplatesTable(): Promise<void> {
        await refreshEmailTemplateTable({
            table: this.templatesTable,
            reloadResource: () => this.templatesResource.reloadAndAwait(),
        });
    }

    async save(): Promise<void> {
        if (this.saving()) {
            return;
        }
        const templateName = this.formTemplateName().trim();
        if (!templateName) {
            await AlertService.alert("Template name is required.");
            return;
        }
        this.saving.set(true);
        try {
            const payload = this.buildPayload();
            const editingTargetId = this.editingId();
            let saved: SeEmailTemplateDto;
            if (editingTargetId != null) {
                saved = await Connect25EmailTemplateApi.updateInstitutionTemplate(payload);
                const accountIds = idsFromDropdownItems(this.formOrgAllowlist());
                await Connect25EmailTemplateApi.putOrgAllowlists([
                    { emailTemplateId: saved.emailTemplateId ?? editingTargetId, accountIds },
                ]);
                TelemetryService.sendWithSub("EmailTemplateSettings", "Admin", "SaveAllowlist");
            } else {
                saved = await Connect25EmailTemplateApi.createInstitutionTemplate(payload);
            }
            TelemetryService.sendWithSub("EmailTemplateSettings", "Admin", editingTargetId != null ? "Save" : "Create");
            await this.refreshTemplatesTable();
            const savedId = saved.emailTemplateId;
            if (savedId == null) {
                this.backToList();
                return;
            }
            this.applySavedTemplateToForm(saved);
            const savedKey = String(savedId);
            this.lastSyncedRouteKey = savedKey;
            if (this.routeTemplateKeyFromStore() !== savedKey) {
                void StateService.go(
                    S25States.names.seEmailTemplateSettings,
                    { emailTemplateId: savedKey },
                    { location: "replace" },
                );
            }
            this.saveSuccessMessage.set(editingTargetId != null ? "Changes saved." : "Template created.");
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        } finally {
            this.saving.set(false);
        }
    }

    async confirmDelete(template: SeEmailTemplateDto): Promise<void> {
        const templateId = template.emailTemplateId;
        if (templateId == null || !Number.isFinite(templateId)) {
            return;
        }
        const confirmed = await AlertService.confirm(
            `Delete template "${template.templateName ?? templateId}"? This cannot be undone.`,
        );
        if (!confirmed) {
            return;
        }
        try {
            await Connect25EmailTemplateApi.deleteInstitutionTemplate(templateId);
            TelemetryService.sendWithSub("EmailTemplateSettings", "Admin", "Delete");
            if (this.editingId() === templateId) {
                this.backToList();
            }
            await this.refreshTemplatesTable();
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        }
    }
}
